import { AlertTriangleIcon, CheckIcon, CopyIcon, LockIcon } from "lucide-react";
import type * as React from "react";
import { useState } from "react";
import { cn } from "../lib/cn";
import { Button } from "./button";

/** FR-INB-07: what is missing and the one next step. */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-6 py-14 text-center",
        className,
      )}
    >
      {icon ? (
        <div className="flex size-12 items-center justify-center rounded-xl bg-muted [&_svg]:size-5">
          {icon}
        </div>
      ) : null}
      <h2 className="font-semibold text-base">{title}</h2>
      {description ? (
        <p className="max-w-sm text-muted-foreground text-sm leading-relaxed">{description}</p>
      ) : null}
      {action}
    </div>
  );
}

/** Errors say what to do next and show the request id, so a report can quote it. */
export function ErrorState({
  title = "Something went wrong",
  message,
  requestId,
  onRetry,
  className,
}: {
  title?: string;
  message?: string | undefined;
  requestId?: string | undefined;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={cn("flex flex-col items-center gap-3 px-6 py-14 text-center", className)}
    >
      <div className="flex size-12 items-center justify-center rounded-xl bg-danger-soft text-danger-ink">
        <AlertTriangleIcon className="size-5" />
      </div>
      <h2 className="font-semibold text-base">{title}</h2>
      {message ? <p className="max-w-sm text-muted-foreground text-sm">{message}</p> : null}
      {requestId ? (
        <p className="font-mono text-muted-foreground text-xs">Request {requestId}</p>
      ) : null}
      {onRetry ? (
        <Button variant="outline" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

/** Shown instead of a page or control the role cannot use. */
export function NoPermission({
  message = "Ask an admin of this workspace if you need it.",
}: {
  message?: string;
}) {
  return (
    <EmptyState icon={<LockIcon />} title="You do not have access to this" description={message} />
  );
}

/** A read-only value with a copy button (links, keys shown once). */
export function CopyField({
  value,
  label,
  className,
}: {
  value: string;
  label: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div
      className={cn("flex items-center gap-2 rounded-md border bg-muted/40 p-1 pl-3", className)}
    >
      <code title={label} className="min-w-0 flex-1 truncate font-mono text-[12.5px]">
        {value}
      </code>
      <Button size="sm" variant="outline" onClick={copy} aria-label={`Copy ${label}`}>
        {copied ? <CheckIcon /> : <CopyIcon />}
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}

/** Label and value rows (Info panel, network detail). */
export function KeyValueList({
  items,
  className,
}: {
  items: { label: string; value: React.ReactNode; mono?: boolean }[];
  className?: string;
}) {
  return (
    <dl
      className={cn(
        "grid grid-cols-[minmax(110px,max-content)_1fr] gap-x-4 gap-y-2 text-[13px]",
        className,
      )}
    >
      {items.map((item) => (
        <div key={item.label} className="contents">
          <dt className="text-muted-foreground">{item.label}</dt>
          <dd className={cn("min-w-0 break-words", item.mono && "font-mono text-[12.5px]")}>
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
